import { ChangeEvent, MutableRefObject, useEffect, useState } from "react";
import { Form, Input } from "antd";
import { styled, css } from "styled-components";
import { LoadingOutlined } from "@ant-design/icons";
import SearchAudio from "./SearchAudio";
import SearchAudioWave from "./SearchAudioWave";
import { isEmpty } from "@dataone/lib";
import { SearchIcon } from "@dataone/icons";

interface SearchInputProps {
  stepRef2: MutableRefObject<null>;
  placeholder: string;
  isSearchLoading: boolean;
  handleSearchChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleConfirmValue: (value: string) => void;
  handleInputFocus: () => void;
  inputValue?: { message: string };
  disabled?: boolean;
  showComponents: boolean;
  showResponse: boolean;
}

const SearchInput = ({
  stepRef2,
  placeholder,
  isSearchLoading,
  inputValue,
  handleSearchChange,
  handleConfirmValue,
  handleInputFocus,
  disabled,
  showComponents,
  showResponse,
}: SearchInputProps) => {
  const [recordingStatus, setRecordingStatus] = useState<boolean | undefined>(
    false
  );
  const [searchForm] = Form.useForm();
  const [microphonePermissionGranted, setMicrophonePermissionGranted] =
    useState(false);

  useEffect(() => {
    if (recordingStatus) searchForm.resetFields();
  }, [recordingStatus]);

  useEffect(() => {
    searchForm.setFieldsValue({ search: inputValue?.message || "" });
    if (inputValue && !isEmpty(inputValue?.message))
      handleConfirmValue(inputValue.message);
  }, [inputValue]);

  const handleMicrophonePermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setMicrophonePermissionGranted(true);
      stream.getTracks().forEach((track) => track.stop());
    } catch (error) {
      console.error("Error accessing microphone:", error);
      setMicrophonePermissionGranted(false);
      setRecordingStatus(false);
    }
  };

  useEffect(() => {
    if (recordingStatus === true) {
      handleMicrophonePermission();
    }
  }, []);

  return (
    <StyledSearch
      $isRecording={!!recordingStatus}
      $showComponents={showComponents}
      ref={stepRef2}
      $showResponse={showResponse}
      disabled={disabled}
    >
      <Form form={searchForm}>
        <Form.Item name="search">
          <Input
            maxLength={255}
            placeholder={recordingStatus ? "I'm listening" : placeholder}
            disabled={disabled || recordingStatus}
            autoComplete="off"
            bordered={false}
            prefix={
              isSearchLoading ? (
                <LoadingOutlined />
              ) : (
                <StyledSearchIcon>
                  <SearchIcon />
                </StyledSearchIcon>
              )
            }
            suffix={
              <SuffixDiv $recordingStatus={!!recordingStatus}>
                {recordingStatus && microphonePermissionGranted && (
                  <SearchAudioWave />
                )}
                <div
                  {...(!recordingStatus &&
                    !disabled && {
                      onClick: (event) => {
                        event.stopPropagation();
                        setRecordingStatus(true);
                      },
                    })}
                  {...(disabled && { style: { opacity: "0.7" } })}
                >
                  <SearchAudio
                    onWhisperSuccess={(search) => {
                      searchForm.setFieldsValue({ search });
                      handleConfirmValue(searchForm.getFieldValue("search"));
                    }}
                    recordingStatus={!!recordingStatus}
                    setRecordingStatus={setRecordingStatus}
                    handleMicrophonePermission={handleMicrophonePermission}
                    disabled={disabled}
                  />
                </div>
              </SuffixDiv>
            }
            onChange={handleSearchChange}
            onPressEnter={() =>
              handleConfirmValue(searchForm.getFieldValue("search"))
            }
            onFocus={handleInputFocus}
          />
        </Form.Item>
      </Form>
      {/* {showResponse && (
        <StyledReset onClick={hideResponse}>
          <SearchPlusIcon />
        </StyledReset>
      )} */}
    </StyledSearch>
  );
};
export default SearchInput;

const StyledSearch = styled.div<{
  $isRecording: boolean;
  $showComponents: boolean;
  $showResponse: boolean;
  disabled?: boolean;
}>`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  margin-top: 3%;
  // border-radius: 40px;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  opacity: ${({ disabled }) => (disabled ? "0.7" : "1")};
  border-radius: ${({ $showComponents }) =>
    $showComponents ? "30px 30px 0 0" : "30px"};
  background: #eff1fd;
  padding: 0px 15px;
  width: ${({ $showResponse }) => ($showResponse ? "70vw" : "580px")};
  max-width: 1300px;
  .ant-form {
    width: 100%;
    border-bottom: ${({ $showComponents }) =>
      $showComponents ? "3px solid #fff" : "none"};
    margin-bottom: ${({ $showComponents }) =>
      $showComponents ? "5px" : "0px"};
    .ant-form-item {
      margin: 0;
    }
  }
  .anticon-loading {
    font-size: 23px;
    margin-right: 10px;
  }
  .ant-input-affix-wrapper {
    font-weight: 400 !important;
    font-size: 14px !important;
    padding: 0px 5px 0px 10px !important;
    height: 42px;
    ::placeholder {
      font-weight: 400 !important;
      font-size: 14px !important;
      line-height: 26px !important;
      ${({ $isRecording }) => $isRecording && "color: #4974f6"};
      color: rgba(4, 7, 38, 0.75);
    }
  }
  .ant-input-affix-wrapper-disabled,
  .ant-input[disabled] {
    cursor: default;
  }
  .ant-input-suffix {
    max-width: calc(100% - 150px);
  }
  .ant-input-prefix {
    display: flex;
    align-items: center;
    img {
      margin-right: 8px;
      width: 14px;
      height: 14px;
    }
  }
`;
const SuffixDiv = styled.div<{ $recordingStatus: boolean }>`
  display: flex;
  gap: 20px;
  flex-direction: row;
  align-items: center;
  padding-left: 70px;
  ${({ $recordingStatus }) =>
    $recordingStatus &&
    css`
      padding-left: 0px;
    `}
  img {
    cursor: pointer;
  }
`;
const StyledSearchIcon = styled.div`
  display: flex;
  font-size: 18px;
  padding-right: 6px;
  color: #000;
`;

const StyledReset = styled.div`
  display: flex;
  justify-content: center;
  cursor: pointer;
  font-size: 17px;
  border-radius: 50%;
  background: #4974f6;
  color: #fff;
  // margin-left: auto;
  padding: 10px;
`;
